import type { WorkflowTrace } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import { canonicalWorkflowTraceToMutableWorkflowTrace } from '@toolproof-core/lib/_pure/workflow-canonicalization';
import { generateIdentifier } from '@toolproof-core/lib/identifier-generation';
import type { TimestampByHandleByType } from '@toolproof-core/lib/_contract/resources';

import { isWorkflowTrace } from '@/workflow-runner/_pure/model/traceHelpers';
import {
    buildBaseTraceSnapshot,
    buildTraceSnapshotFromBase,
} from '@/workflow-runner/_pure/model/traceSnapshotBuilder';
import { selectActiveToolStepFromTraceSnapshot } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import { curatedWorkflowTraces } from '@/workflow-runner/curatedTraces';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { TraceSnapshotResolver } from '@/workflow-runner/_contract/pipelineContext';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';
import type { TraceSnapshot } from '@/workflow-runner/_contract/traceSnapshot';

// Re-export pure selector so existing callers don't need to change import paths.
export { selectActiveToolStepFromTraceSnapshot as selectActiveToolStepFromTraceSnapshot };

const TYPE_WorkflowTrace = CONSTANTS.Handles.ResourceTypes.TYPE_WorkflowTrace;

export function toSortedWorkflowTraces(args: {
    resourcesByType: CosmosDataModel['resourcesByType'];
    timestampByHandleByType: TimestampByHandleByType;
    localWorkflowTraces?: CosmosDataModel['localWorkflowTraces'];
}, ephemeralWorkflowTraces: WorkflowTrace[] = []): WorkflowTrace[] {
    const { resourcesByType, timestampByHandleByType } = args;
    const out: WorkflowTrace[] = [];

    const selectedSource = args.localWorkflowTraces
        ? args.localWorkflowTraces.traces
        : (resourcesByType[TYPE_WorkflowTrace] ?? [])
            .map((resource) => resource.projection)
            .filter(isWorkflowTrace);
    const traceByHandle = new Map<string, { trace: WorkflowTrace; source: string }>();
    const addTrace = (trace: WorkflowTrace, source: string) => {
        const previous = traceByHandle.get(trace.handle);
        if (previous) {
            if (stableWorkflowTracePayload(previous.trace) !== stableWorkflowTracePayload(trace)) {
                throw new Error(`Conflicting WorkflowTrace payloads for handle '${trace.handle}' in ${previous.source} and ${source}.`);
            }
            return;
        }
        traceByHandle.set(trace.handle, { trace, source });
        out.push(trace);
    };

    for (const trace of ephemeralWorkflowTraces) addTrace(trace, 'ephemeral');
    for (const trace of selectedSource) addTrace(trace, args.localWorkflowTraces ? 'local' : 'remote');
    for (const trace of curatedWorkflowTraces) addTrace(trace, 'curated');

    function stableWorkflowTracePayload(trace: WorkflowTrace): string {
        return JSON.stringify(trace, (_key, value: unknown) => {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                return Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)));
            }
            return value;
        });
    }

    const workflowTraceTimestampByHandle = args.localWorkflowTraces?.timestampByHandle ?? timestampByHandleByType[TYPE_WorkflowTrace] ?? {};
    out.sort((a, b) => {
        const aEphemeral = ephemeralWorkflowTraces.indexOf(a);
        const bEphemeral = ephemeralWorkflowTraces.indexOf(b);
        if (aEphemeral !== -1 || bEphemeral !== -1) {
            return aEphemeral === -1 ? 1 : bEphemeral === -1 ? -1 : aEphemeral - bEphemeral;
        }
        const aTimestamp = workflowTraceTimestampByHandle[a.handle];
        const bTimestamp = workflowTraceTimestampByHandle[b.handle];

        if (typeof aTimestamp === 'string' && typeof bTimestamp === 'string') {
            const timestampCmp = bTimestamp.localeCompare(aTimestamp);
            if (timestampCmp !== 0) return timestampCmp;
        } else if (typeof aTimestamp === 'string') {
            return -1;
        } else if (typeof bTimestamp === 'string') {
            return 1;
        }

        return b.handle.localeCompare(a.handle);
    });

    return out;
} // ATTENTION: move behind _pure/ boundary?

export function createTraceSnapshotResolver(): TraceSnapshotResolver {
    let selectedSource: WorkflowTrace | undefined;
    let selectedToolMap: CosmosDataModel['toolMap'] | undefined;
    let baseSnapshot: ReturnType<typeof buildBaseTraceSnapshot> | undefined;
    const cachedByPosition = new Map<string, TraceSnapshot>();
    const clear = () => {
        selectedSource = undefined;
        selectedToolMap = undefined;
        baseSnapshot = undefined;
        cachedByPosition.clear();
    };

    const deriveTraceSnapshot = (args: {
        cosmosData: CosmosDataModel;
        runtimeState: RuntimeState;
    }): TraceSnapshot | undefined => {
        const traceSelection = args.runtimeState.traceSelection;
        const workflowTraces = toSortedWorkflowTraces(args.cosmosData, args.runtimeState.ephemeralWorkflowTraces);

        const selectedTrace =
            traceSelection?.workflowTraceSelectionKind === 'by-handle' && typeof traceSelection.workflowTraceHandle === 'string'
                ? (workflowTraces.find((trace) => trace.handle === traceSelection.workflowTraceHandle) ?? workflowTraces[0])
                : workflowTraces[0];
        if (!selectedTrace) {
            clear();
            return undefined;
        }
        if (selectedSource !== selectedTrace || selectedToolMap !== args.cosmosData.toolMap) {
            clear();
            selectedSource = selectedTrace;
            selectedToolMap = args.cosmosData.toolMap;
        }

        const replayPositionIndex = traceSelection?.replayPositionIndex;

        // Base snapshot: mutable-trace derivation keyed by canonical trace identity.
        if (!baseSnapshot) {
            const mutableWorkflowTrace = canonicalWorkflowTraceToMutableWorkflowTrace(selectedTrace, generateIdentifier);
            baseSnapshot = buildBaseTraceSnapshot({
                mutableWorkflowTrace,
                selectedTraceHandle: selectedTrace.handle,
            });
        }

        // Per-position snapshot: keyed by applied position index within this trace.
        const replayPositionCount = baseSnapshot.timeline.toolExecutionTickIndexes.length;
        const appliedReplayPositionIndex = replayPositionIndex === undefined
            ? replayPositionCount
            : Math.min(Math.max(Math.trunc(replayPositionIndex), 0), replayPositionCount);
        const positionCacheKey = `${appliedReplayPositionIndex}`;

        const cached = cachedByPosition.get(positionCacheKey);
        if (cached) return cached;

        const snapshot = buildTraceSnapshotFromBase({
            baseSnapshot,
            replayPositionIndex,
            toolMap: args.cosmosData.toolMap,
        });

        cachedByPosition.set(positionCacheKey, snapshot);
        return snapshot;
    };

    const deriveTraceSnapshotAtPosition = (args: {
        cosmosData: CosmosDataModel;
        runtimeState: RuntimeState;
        replayPositionIndex: number;
    }): TraceSnapshot | undefined => {
        const { cosmosData, runtimeState, replayPositionIndex } = args;

        return deriveTraceSnapshot({
            cosmosData,
            runtimeState: {
                ...runtimeState,
                traceSelection: {
                    ...runtimeState.traceSelection,
                    replayPositionIndex,
                },
            },
        });
    };

    return {
        deriveTraceSnapshot,
        deriveTraceSnapshotAtPosition,
        clear,
    };
}
